var { normalizeConditionRule, OPERATOR_LABELS, operatorRequiresValue } = require('./conditionRule');
var { anchorDir } = require('./connectionPaths');
var { coerceValueForVariableType, computeSetVariableResult, formatValueForStorage, isTruncatedShapeValue } = require('./setVariableRuntime');
var { isToolAgentNode, isToolConnection } = require('./nodeTypes');

var fallbackSizes = {
  userInitializing: { width: 260, height: 72 },
  initializeData: { width: 145, height: 32 },
  setupAutomation: { width: 172, height: 32 },
  dataCollection: { width: 292, height: 148 },
  triggerAutomation: { width: 292, height: 148 },
  executeTriggered: { width: 174, height: 32 },
  dataValidation: { width: 308, height: 148 },
  combineResults: { width: 164, height: 32 },
  actionTrigger: { width: 308, height: 148 },
  outputGeneration: { width: 308, height: 148 },
  finalize: { width: 96, height: 32 },
  actionCompletion: { width: 308, height: 88 }
};

var FUTURE_CONNECTOR_BUTTON_WIDTH = 214;
var FUTURE_CONNECTOR_BUTTON_HEIGHT = 54;

function cloneTemplateNode(template, id, position) {
  return {
    id: id,
    kind: template.kind,
    nodeType: template.nodeType,
    category: template.category || null,
    title: template.title,
    subtitle: template.subtitle,
    icon: template.icon,
    logoUrl: template.logoUrl || null,
    templateId: template.id || null,
    code: template.code || '',
    inputDescription: template.inputDescription ? String(template.inputDescription) : '',
    agentDescription: template.agentDescription ? String(template.agentDescription) : '',
    inputExample: template.inputExample ? String(template.inputExample) : '',
    outputDescription: template.outputDescription ? String(template.outputDescription) : '',
    generationPrompt: template.generationPrompt ? String(template.generationPrompt) : '',
    changeHistory: Array.isArray(template.changeHistory) ? template.changeHistory.slice() : [],
    label: template.label,
    color: template.color,
    borderColor: template.borderColor,
    options: Array.isArray(template.options) ? template.options.slice() : null,
    stats: Array.isArray(template.stats)
      ? template.stats.map(function(stat) {
          return Object.assign({}, stat);
        })
      : null,
    hyperparameters: Array.isArray(template.hyperparameters)
      ? template.hyperparameters.map(function(p) { return Object.assign({}, p); })
      : null,
    conditionRule: normalizeConditionRule(template.conditionRule),
    size: template.size
      ? {
          width: template.size.width,
          height: template.size.height
        }
      : null,
    paramValues: Array.isArray(template.hyperparameters)
      ? template.hyperparameters.reduce(function(acc, p) {
          acc[p.key] = p.default !== undefined ? p.default : '';
          return acc;
        }, {})
      : {},
    position: position
  };
}

function getFutureNodeSize(template) {
  // Default falls back to the new compact tile dimensions (~132x132). The old
  // 220x80 default was sized for the wider rectangular cards and made future-
  // node placement land too far away from the source anchor.
  var size = template && template.size ? template.size : null;
  return {
    width: size && Number.isFinite(size.width) ? size.width : 132,
    height: size && Number.isFinite(size.height) ? size.height : 132
  };
}

function getFutureNodePosition(template, futureConnector) {
  var dir = anchorDir(futureConnector.anchor);
  var size = getFutureNodeSize(template);
  var gapX = 46;
  var gapY = 36;

  if (dir.x > 0) {
    return {
      left: Math.round(futureConnector.to.x + gapX),
      top: Math.round(futureConnector.to.y - (size.height / 2))
    };
  }

  if (dir.x < 0) {
    return {
      left: Math.round(futureConnector.to.x - gapX - size.width),
      top: Math.round(futureConnector.to.y - (size.height / 2))
    };
  }

  if (dir.y > 0) {
    return {
      left: Math.round(futureConnector.to.x - (size.width / 2)),
      top: Math.round(futureConnector.to.y + gapY)
    };
  }

  return {
    left: Math.round(futureConnector.to.x - (size.width / 2)),
    top: Math.round(futureConnector.to.y - gapY - size.height)
  };
}

function compactSetVariableTitle(node) {
  var pv = node && node.paramValues ? node.paramValues : {};
  var targetVar = String(pv.variable || '').trim();
  if (!targetVar) return 'Set Variable';
  return 'Set ' + targetVar;
}

function compactConditionTitle(node) {
  var pv = node && node.paramValues ? node.paramValues : {};
  var rule = node && node.conditionRule ? node.conditionRule : null;
  var varName = String(pv.variable || '').trim();
  var normalized = normalizeConditionRule(rule);
  var first = normalized.conditions[0] || null;
  if (!first) return 'Condition';
  var opLabel = OPERATOR_LABELS[first.operator] || '=';
  var val = String(first.value || '').trim();
  var parts = [];
  if (varName) parts.push(varName);
  parts.push(opLabel);
  if (operatorRequiresValue(first.operator)) {
    parts.push(val || '?');
  }
  return parts.join(' ') || 'Condition';
}

var AUTO_FIELD_STOP_WORDS = ['and','or','the','a','an','to','from','for','with','in','of','by','that','is','are','return','returns','get','give','me','please','i','want','need','should','will','can','do','make','create','build','then','after','before','as','it','its','this','that','each','all','any','into','out','up','use','using','call','calls','take','takes','send','sends'];

function autoFieldName(promptText) {
  var text = String(promptText || '').toLowerCase();
  var words = text.match(/[a-z]+/g) || [];
  var meaningful = words.filter(function(w) { return w.length > 2 && AUTO_FIELD_STOP_WORDS.indexOf(w) === -1; });
  var slug = meaningful.slice(0, 3).join('_');
  return slug.slice(0, 30) || 'result';
}

function buildGeneratedAgentTemplate(data, responseFields, generationPrompt) {
  var timestampId = Date.now().toString(36);
  var safeData = data || {};
  var title = String(safeData.title || '').trim() || 'AI Agent';
  var fields = Array.isArray(responseFields) ? responseFields : [];
  var validFields = fields.map(function(f) { return String(f || '').trim(); }).filter(Boolean);
  if (!validFields.length) validFields = ['result'];
  var responseTemplate = validFields.map(function(name) { return { name: name, description: '' }; });
  var agentConfig = {
    system: String(safeData.system || 'You are a helpful AI assistant.').trim(),
    temperature: typeof safeData.temperature === 'number' ? safeData.temperature : 0.7,
    maxTokens: 8000,
    variableInputs: Array.isArray(safeData.variableInputs) ? safeData.variableInputs : [],
    responseTemplate: responseTemplate
  };
  return {
    id: 'agent-' + timestampId,
    kind: 'workflow-node',
    nodeType: 'card',
    category: 'system',
    title: title,
    subtitle: 'AI node',
    icon: 'agent',
    label: title,
    borderColor: '',
    conditionRule: null,
    options: null,
    stats: null,
    code: '',
    generationPrompt: String(generationPrompt || '').trim(),
    changeHistory: [],
    _agentConfig: agentConfig,
    hyperparameters: [
      { id: 'agent-system', key: 'system', type: 'string', default: agentConfig.system },
      { id: 'agent-prompt', key: 'prompt', type: 'string', default: '' },
      { id: 'agent-instruction', key: 'instruction', type: 'string', default: '' },
      { id: 'agent-temperature', key: 'temperature', type: 'number', default: agentConfig.temperature },
      { id: 'agent-max-tokens', key: 'maxTokens', type: 'number', default: 8000 }
    ],
    size: null
  };
}

function buildGeneratedCustomTemplate(data) {
  var timestampId = Date.now().toString(36);
  var safeData = data || {};
  var rawName = String(safeData.name || safeData.title || '').trim();
  var name = rawName || ('AI Node ' + timestampId);
  var generationWarning = String(safeData.warning || '').trim();
  return {
    id: 'custom-' + timestampId,
    kind: 'workflow-node',
    nodeType: 'card',
    category: 'custom',
    title: name,
    subtitle: generationWarning ? ('Warning: ' + generationWarning) : 'Custom node',
    logoUrl: String(safeData.logoUrl || '').trim(),
    code: String(safeData.code || ''),
    // Tool nodes: agent-facing fields the Tool Agent reads at runtime — what
    // the tool does, what value to pass as its input (+ a concrete example),
    // and what the result looks like. Empty for normal nodes.
    inputDescription: String(safeData.inputDescription || '').trim(),
    agentDescription: String(safeData.agentDescription || '').trim(),
    inputExample: String(safeData.inputExample || '').trim(),
    outputDescription: String(safeData.outputDescription || '').trim(),
    generationPrompt: String(safeData.generationPrompt || '').trim(),
    changeHistory: Array.isArray(safeData.changeHistory) ? safeData.changeHistory : [],
    hyperparameters: Array.isArray(safeData.hyperparameters) ? safeData.hyperparameters : [],
    icon: 'custom',
    conditionRule: null,
    options: null,
    stats: null,
    borderColor: '',
    label: name
  };
}

function remapClassifierConnections(connections, nodeId, oldLabelsStr, newLabelsStr) {
  var oldLabels = String(oldLabelsStr || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
  var newLabels = String(newLabelsStr || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
  var indexMap = {};
  oldLabels.forEach(function(label, oldIdx) {
    var newIdx = newLabels.indexOf(label);
    if (newIdx >= 0) indexMap[oldIdx] = newIdx;
  });
  return (connections || []).filter(function(conn) {
    if (conn.from !== nodeId) return true;
    if (typeof conn.fromAnchor !== 'string' || conn.fromAnchor.indexOf('classifier-') !== 0) return true;
    var oldIdx = Number(conn.fromAnchor.split('-')[1]);
    return indexMap[oldIdx] !== undefined;
  }).map(function(conn) {
    if (conn.from !== nodeId) return conn;
    if (typeof conn.fromAnchor !== 'string' || conn.fromAnchor.indexOf('classifier-') !== 0) return conn;
    var oldIdx = Number(conn.fromAnchor.split('-')[1]);
    if (indexMap[oldIdx] === undefined || indexMap[oldIdx] === oldIdx) return conn;
    return Object.assign({}, conn, { fromAnchor: 'classifier-' + indexMap[oldIdx] });
  });
}

// Walks outgoing edges from startNodeId and returns reachable node ids and
// the connection ids along the way. Used to highlight the downstream "flow"
// when the user hovers a node. Pure function — safe for useMemo.
// maxDepth limits how far to walk: 1 = direct outgoing edges only (default),
// Infinity = full downstream subgraph.
function computeDownstreamFlow(startNodeId, connections, maxDepth) {
  var nodeIds = {};
  var connIds = {};
  if (startNodeId == null || !Array.isArray(connections) || connections.length === 0) {
    return { nodeIds: nodeIds, connIds: connIds };
  }
  var limit = typeof maxDepth === 'number' && maxDepth > 0 ? maxDepth : 1;
  var adjacency = {};
  for (var i = 0; i < connections.length; i++) {
    var c = connections[i];
    if (!c || c.from == null || c.to == null) continue;
    var key = String(c.from);
    if (!adjacency[key]) adjacency[key] = [];
    adjacency[key].push({ toId: String(c.to), connId: c.id });
  }
  var startKey = String(startNodeId);
  var visited = {};
  visited[startKey] = true;
  var queue = [{ id: startKey, depth: 0 }];
  while (queue.length) {
    var entry = queue.shift();
    if (entry.depth >= limit) continue;
    var outgoing = adjacency[entry.id];
    if (!outgoing) continue;
    for (var j = 0; j < outgoing.length; j++) {
      var edge = outgoing[j];
      if (edge.connId != null) connIds[edge.connId] = true;
      if (!visited[edge.toId]) {
        visited[edge.toId] = true;
        nodeIds[edge.toId] = true;
        queue.push({ id: edge.toId, depth: entry.depth + 1 });
      }
    }
  }
  return { nodeIds: nodeIds, connIds: connIds };
}

// ── Auto-layout ────────────────────────────────────────────────────────────
// Re-arranges nodes into a clean left-to-right layered flow by following the
// connection graph. Same algorithm the planner uses after generating a flow,
// extracted here so the "Format" button can replay it on demand.
//
// Variables nodes (and any node with icon === 'variables') are left untouched
// — they're sidebar-style and shouldn't be pulled into the flow grid.
//
// Returns { nodes, connections } where:
//   - flow nodes get fresh `position` values on a layered grid
//   - non-classifier / non-condition connection anchors are re-aimed at the
//     nearest side based on the new relative positions
//   - any old bend points are cleared so the renderer's live obstacle-
//     avoidance routing has a clean slate to work from
function autoLayoutWorkflow(rawNodes, rawConnections, options) {
  var opts = options || {};
  // Minimum gap between adjacent nodes (in addition to their own height/width).
  // Classify, Router, and Agent cards can be much taller than a basic tile,
  // so we treat these as floors and add the actual measured node size on top.
  var COLUMN_GAP_X = opts.columnGapX || 80;   // horizontal gutter between layers
  var ROW_GAP_Y = opts.rowGapY || 60;         // vertical gutter between siblings
  // Serpentine wrap: when > 0, the layered columns fold into multiple rows
  // after this many columns, each row snaking back the other way, so a long
  // mostly-linear workflow stays compact instead of sprawling off to the right.
  var WRAP_COLUMNS = opts.wrapColumns || 0;
  var BAND_GAP_Y = opts.bandGapY || 90;       // vertical gutter between wrapped rows
  var DEFAULT_NODE_W = opts.defaultNodeWidth || 150;
  var DEFAULT_NODE_H = opts.defaultNodeHeight || 110;
  var START_X = opts.startX || 100;
  var START_Y = opts.startY || 250;
  var sizes = opts.sizes || {};

  function nodeWidth(n) {
    var s = n && sizes[n.id];
    if (s && s.width) return s.width;
    return DEFAULT_NODE_W;
  }
  function nodeHeight(n) {
    var s = n && sizes[n.id];
    if (s && s.height) return s.height;
    return DEFAULT_NODE_H;
  }

  var nodes = (rawNodes || []).map(function(n) { return Object.assign({}, n); });
  var connections = (rawConnections || []).map(function(c) { return Object.assign({}, c); });

  var nodeByIdAll = {};
  for (var nb = 0; nb < nodes.length; nb++) nodeByIdAll[nodes[nb].id] = nodes[nb];

  // Tool wires (Tool Agent bottom anchor → a tool node) are NOT flow edges —
  // the tool hangs below its agent and is invoked per-call. Collect each
  // agent's tool node ids so the layered grid skips them entirely; they're
  // re-stacked beneath their agent at the end instead.
  var toolNodeIds = {};                 // toolNodeId -> agentNodeId
  var toolsByAgent = {};                // agentNodeId -> [toolNodeId, ...] (conn order)
  for (var tc = 0; tc < connections.length; tc++) {
    var tconn = connections[tc];
    var tfrom = nodeByIdAll[tconn.from];
    if (!isToolConnection(tconn, tfrom)) continue;
    toolNodeIds[tconn.to] = tconn.from;
    if (!toolsByAgent[tconn.from]) toolsByAgent[tconn.from] = [];
    if (toolsByAgent[tconn.from].indexOf(tconn.to) === -1) toolsByAgent[tconn.from].push(tconn.to);
  }

  // Flow grid = everything except sidebar-style variables nodes AND tool nodes
  // (those are placed relative to their agent, below).
  var flowNodes = nodes.filter(function(n) {
    return n && n.icon !== 'variables' && !toolNodeIds[n.id];
  });
  if (flowNodes.length === 0) return { nodes: nodes, connections: connections };

  var nodeById = {};
  var childrenMap = {};
  var parentMap = {};
  for (var i = 0; i < flowNodes.length; i++) {
    var fid = flowNodes[i].id;
    nodeById[fid] = flowNodes[i];
    childrenMap[fid] = [];
    parentMap[fid] = [];
  }
  // Track original connection order — we use it to keep YES/NO and
  // classifier branches in the order the user authored them.
  var connOrderFromTo = {};
  for (var ci = 0; ci < connections.length; ci++) {
    var c = connections[ci];
    if (!nodeById[c.from] || !nodeById[c.to]) continue;
    // Tool wires never participate in flow layering (the tool is excluded from
    // flowNodes anyway, but guard here too in case both endpoints survive).
    if (toolNodeIds[c.to]) continue;
    if (childrenMap[c.from].indexOf(c.to) === -1) childrenMap[c.from].push(c.to);
    if (parentMap[c.to].indexOf(c.from) === -1) parentMap[c.to].push(c.from);
    var key = c.from + '->' + c.to;
    if (connOrderFromTo[key] === undefined) connOrderFromTo[key] = ci;
  }

  // Layer assignment — BFS from the Start node so Start is always at layer 0
  // and the rest fan out by graph distance. This avoids the longest-path
  // pitfall where a back-edge or shortcut could pull Start into a middle
  // layer. Disconnected subgraphs (rare, usually orphan branches) are
  // assigned afterward via longest-path from their own roots.
  var layerOf = {};
  var startNode = null;
  for (var si = 0; si < flowNodes.length; si++) {
    if (flowNodes[si].icon === 'start') { startNode = flowNodes[si]; break; }
  }

  function bfsFrom(rootId) {
    var queue = [{ id: rootId, layer: 0 }];
    var seen = {};
    while (queue.length) {
      var item = queue.shift();
      if (seen[item.id]) continue;
      seen[item.id] = true;
      // Keep the deepest layer assignment so a node reached via a longer
      // path still sits to the right of all its parents.
      if (layerOf[item.id] === undefined || item.layer > layerOf[item.id]) {
        layerOf[item.id] = item.layer;
      }
      var kids = childrenMap[item.id] || [];
      for (var ki = 0; ki < kids.length; ki++) {
        queue.push({ id: kids[ki], layer: item.layer + 1 });
      }
    }
  }

  if (startNode) bfsFrom(startNode.id);

  // Any node not yet placed belongs to a disconnected component. Pick its
  // own roots (no parents inside the unplaced set) and BFS from each.
  for (var ui = 0; ui < flowNodes.length; ui++) {
    var unId = flowNodes[ui].id;
    if (layerOf[unId] !== undefined) continue;
    var parents = parentMap[unId] || [];
    var hasUnplacedParent = false;
    for (var pi = 0; pi < parents.length; pi++) {
      if (layerOf[parents[pi]] === undefined) { hasUnplacedParent = true; break; }
    }
    if (!hasUnplacedParent) bfsFrom(unId);
  }
  // Catch-all for nodes inside cycles that never got reached.
  for (var fi = 0; fi < flowNodes.length; fi++) {
    if (layerOf[flowNodes[fi].id] === undefined) layerOf[flowNodes[fi].id] = 0;
  }

  // Bucket by layer.
  var layers = {};
  var maxLayer = 0;
  for (var gi = 0; gi < flowNodes.length; gi++) {
    var lyr = layerOf[flowNodes[gi].id] || 0;
    if (!layers[lyr]) layers[lyr] = [];
    layers[lyr].push(flowNodes[gi]);
    if (lyr > maxLayer) maxLayer = lyr;
  }

  // Initial within-layer order — by parent's order at layer 0 (Start first),
  // and for deeper layers by the order their first parent connects to them.
  // This mirrors how a user reads a flow: the "first" branch out of a
  // splitter sits on top.
  function initialLayerOrder() {
    if (layers[0]) {
      layers[0].sort(function(a, b) {
        if (a.icon === 'start' && b.icon !== 'start') return -1;
        if (b.icon === 'start' && a.icon !== 'start') return 1;
        // Stable: preserve original array order for other roots
        return flowNodes.indexOf(a) - flowNodes.indexOf(b);
      });
    }
    for (var L = 1; L <= maxLayer; L++) {
      var bucket = layers[L];
      if (!bucket) continue;
      bucket.sort(function(a, b) {
        var ap = parentMap[a.id] || [];
        var bp = parentMap[b.id] || [];
        // Compare by smallest connection index from any parent — this puts
        // the YES branch above the NO branch, classifier label 1 above 2,
        // and so on, since the planner emits connections in label order.
        var aIdx = Infinity, bIdx = Infinity;
        for (var ai = 0; ai < ap.length; ai++) {
          var ak = connOrderFromTo[ap[ai] + '->' + a.id];
          if (ak !== undefined && ak < aIdx) aIdx = ak;
        }
        for (var bi = 0; bi < bp.length; bi++) {
          var bk = connOrderFromTo[bp[bi] + '->' + b.id];
          if (bk !== undefined && bk < bIdx) bIdx = bk;
        }
        if (aIdx !== bIdx) return aIdx - bIdx;
        return flowNodes.indexOf(a) - flowNodes.indexOf(b);
      });
    }
  }
  initialLayerOrder();

  // Barycenter sweeps to minimize edge crossings. We do a few alternating
  // down-then-up passes; this is the classic Sugiyama heuristic and is
  // dramatically better than just trusting the previous layout's vertical
  // positions (which is what produced the prior crisscrossed mess when the
  // user formatted an already-scrambled graph).
  function indexOfNodeInLayer(nodeId) {
    var lyr = layerOf[nodeId];
    var bucket = layers[lyr] || [];
    for (var k = 0; k < bucket.length; k++) {
      if (bucket[k].id === nodeId) return k;
    }
    return 0;
  }
  function barycenterDown() {
    for (var L = 1; L <= maxLayer; L++) {
      var bucket = layers[L];
      if (!bucket || bucket.length < 2) continue;
      var bary = {};
      for (var bi = 0; bi < bucket.length; bi++) {
        var node = bucket[bi];
        var ps = parentMap[node.id] || [];
        if (ps.length === 0) { bary[node.id] = bi; continue; }
        var sum = 0, count = 0;
        for (var pi = 0; pi < ps.length; pi++) {
          if (layerOf[ps[pi]] === L - 1) {
            sum += indexOfNodeInLayer(ps[pi]);
            count++;
          }
        }
        bary[node.id] = count > 0 ? sum / count : bi;
      }
      bucket.sort(function(a, b) {
        if (bary[a.id] !== bary[b.id]) return bary[a.id] - bary[b.id];
        return flowNodes.indexOf(a) - flowNodes.indexOf(b);
      });
    }
  }
  function barycenterUp() {
    for (var L = maxLayer - 1; L >= 0; L--) {
      var bucket = layers[L];
      if (!bucket || bucket.length < 2) continue;
      var bary = {};
      for (var bi = 0; bi < bucket.length; bi++) {
        var node = bucket[bi];
        var ks = childrenMap[node.id] || [];
        if (ks.length === 0) { bary[node.id] = bi; continue; }
        var sum = 0, count = 0;
        for (var ki = 0; ki < ks.length; ki++) {
          if (layerOf[ks[ki]] === L + 1) {
            sum += indexOfNodeInLayer(ks[ki]);
            count++;
          }
        }
        bary[node.id] = count > 0 ? sum / count : bi;
      }
      bucket.sort(function(a, b) {
        if (bary[a.id] !== bary[b.id]) return bary[a.id] - bary[b.id];
        return flowNodes.indexOf(a) - flowNodes.indexOf(b);
      });
    }
  }
  for (var sweep = 0; sweep < 4; sweep++) {
    barycenterDown();
    barycenterUp();
  }

  // Final placement.
  //
  // First, measure every layer's vertical extent once (sum of its nodes'
  // heights plus the gaps between stacked siblings). Both modes reuse this.
  var layerHeights = {};   // layer -> [height of each node, in stack order]
  var layerTotalH = {};    // layer -> total stacked height of the layer
  for (var Lh = 0; Lh <= maxLayer; Lh++) {
    var hb = layers[Lh];
    if (!hb) continue;
    var hs = [];
    var tot = 0;
    for (var hi = 0; hi < hb.length; hi++) {
      var hh = nodeHeight(hb[hi]);
      hs.push(hh);
      tot += hh;
    }
    if (hb.length > 1) tot += (hb.length - 1) * ROW_GAP_Y;
    layerHeights[Lh] = hs;
    layerTotalH[Lh] = tot;
  }

  // colX[L]    = the left X shared by every node in layer L.
  // centerY[L] = the Y that layer L's vertical stack is centered on.
  // Two modes fill these in:
  //   • flat (default / tighter spacing): one left-to-right row; every layer
  //     shares START_Y so the chain reads as a single line.
  //   • serpentine: columns fold into rows of WRAP_COLUMNS, each row snaking
  //     back the other way so a long chain stays compact.
  var colX = {};
  var centerY = {};
  var doSerpentine = WRAP_COLUMNS > 0 && (maxLayer + 1) > WRAP_COLUMNS;

  if (!doSerpentine) {
    // Horizontal: each layer's X is the previous layer's X plus the widest
    // node in the previous layer plus COLUMN_GAP_X. This keeps wide cards
    // (Classify, Router, Agent) from running into the next column.
    colX[0] = START_X;
    for (var L1 = 1; L1 <= maxLayer; L1++) {
      var prev = layers[L1 - 1] || [];
      var maxPrevW = 0;
      for (var pi2 = 0; pi2 < prev.length; pi2++) {
        var w = nodeWidth(prev[pi2]);
        if (w > maxPrevW) maxPrevW = w;
      }
      if (maxPrevW === 0) maxPrevW = DEFAULT_NODE_W;
      colX[L1] = colX[L1 - 1] + maxPrevW + COLUMN_GAP_X;
    }
    for (var Lc = 0; Lc <= maxLayer; Lc++) centerY[Lc] = START_Y;
  } else {
    // One uniform column slot (the widest node) so the snake's columns line up
    // vertically across rows and the turn-edges run straight up/down.
    var COL_W = DEFAULT_NODE_W;
    for (var wi = 0; wi < flowNodes.length; wi++) {
      var ww = nodeWidth(flowNodes[wi]);
      if (ww > COL_W) COL_W = ww;
    }
    // A "band" is one wrapped row of up to WRAP_COLUMNS layers. Each band is as
    // tall as its tallest layer; bands stack downward with BAND_GAP_Y between.
    var lastBand = Math.floor(maxLayer / WRAP_COLUMNS);
    var bandHeight = {};
    for (var Lb = 0; Lb <= maxLayer; Lb++) {
      var bandOf = Math.floor(Lb / WRAP_COLUMNS);
      var th = layerTotalH[Lb] || DEFAULT_NODE_H;
      if (!bandHeight[bandOf] || th > bandHeight[bandOf]) bandHeight[bandOf] = th;
    }
    var bandTop = {};
    var yAcc = START_Y;
    for (var bt = 0; bt <= lastBand; bt++) {
      bandTop[bt] = yAcc;
      yAcc += (bandHeight[bt] || DEFAULT_NODE_H) + BAND_GAP_Y;
    }
    for (var Ls = 0; Ls <= maxLayer; Ls++) {
      var bnd = Math.floor(Ls / WRAP_COLUMNS);
      var col = Ls % WRAP_COLUMNS;
      // Even bands flow left→right; odd bands right→left so the last column of
      // one row sits directly above the first column of the next (the turn).
      var slot = (bnd % 2 === 0) ? col : (WRAP_COLUMNS - 1 - col);
      colX[Ls] = START_X + slot * (COL_W + COLUMN_GAP_X);
      centerY[Ls] = bandTop[bnd] + (bandHeight[bnd] || DEFAULT_NODE_H) / 2;
    }
  }

  // Place every node: stack each layer's nodes top-to-bottom, centered on the
  // layer's centerY.
  for (var L2 = 0; L2 <= maxLayer; L2++) {
    var bucket2 = layers[L2];
    if (!bucket2) continue;
    var heights = layerHeights[L2] || [];
    var totalH = layerTotalH[L2] || 0;
    var baseX = colX[L2] != null ? colX[L2] : START_X;
    var cursorY = (centerY[L2] != null ? centerY[L2] : START_Y) - totalH / 2;
    for (var ni = 0; ni < bucket2.length; ni++) {
      bucket2[ni].position = {
        left: Math.round(baseX),
        top: Math.round(cursorY)
      };
      cursorY += (heights[ni] || DEFAULT_NODE_H) + ROW_GAP_Y;
    }
  }

  // Place each Tool Agent's tools as a horizontal row directly BELOW the agent,
  // centered under it — matching the dashed bottom-anchor "tools" structure the
  // renderer draws. This is the one spot tools are positioned; they never enter
  // the layered grid above.
  var TOOL_GAP_Y = 70;   // gap between the agent's bottom and the tool row
  var TOOL_GAP_X = 40;   // gap between adjacent tools in the row
  Object.keys(toolsByAgent).forEach(function(agentId) {
    var agent = nodeByIdAll[agentId];
    if (!agent || !agent.position) return;
    var ids = toolsByAgent[agentId].filter(function(id) { return !!nodeByIdAll[id]; });
    if (ids.length === 0) return;

    var agentLeft = agent.position.left || 0;
    var agentTop = agent.position.top || 0;
    var agentW = nodeWidth(agent);
    var agentH = nodeHeight(agent);
    var agentCenterX = agentLeft + agentW / 2;
    var rowTop = agentTop + agentH + TOOL_GAP_Y;

    // Total row width so the stack centers under the agent.
    var rowW = 0;
    for (var ti = 0; ti < ids.length; ti++) {
      rowW += nodeWidth(nodeByIdAll[ids[ti]]);
      if (ti > 0) rowW += TOOL_GAP_X;
    }
    var cursorX = agentCenterX - rowW / 2;
    for (var tj = 0; tj < ids.length; tj++) {
      var toolNode = nodeByIdAll[ids[tj]];
      toolNode.position = { left: Math.round(cursorX), top: Math.round(rowTop) };
      cursorX += nodeWidth(toolNode) + TOOL_GAP_X;
    }
  });

  // Lowest bottom edge among nodes whose horizontal span overlaps [x1, x2].
  // A long hop routes its under-the-row run just below THESE nodes (the ones
  // it would otherwise cross), not below the whole layout — so the dip stays
  // shallow instead of plunging beneath every row.
  function lowestBottomBetween(x1, x2) {
    var lo = Math.min(x1, x2);
    var hi = Math.max(x1, x2);
    var bottom = -Infinity;
    for (var fb = 0; fb < nodes.length; fb++) {
      var fbn = nodes[fb];
      if (!fbn || !fbn.position) continue;
      var nl = fbn.position.left || 0;
      var nr = nl + nodeWidth(fbn);
      if (nr < lo || nl > hi) continue;            // node's column is outside the span
      var fbBottom = (fbn.position.top || 0) + nodeHeight(fbn);
      if (fbBottom > bottom) bottom = fbBottom;
    }
    return isFinite(bottom) ? bottom : 0;
  }

  // Re-aim connection anchors to the nearest side and clear any old user
  // bend points so the renderer's auto-routing has a clean slate. Skip
  // classifier outputs and condition outputs — those carry semantic
  // meaning (YES/NO, classifier labels) so geometry alone can't pick the
  // right anchor.
  for (var ai = 0; ai < connections.length; ai++) {
    var conn = connections[ai];
    var fromNode = nodeByIdAll[conn.from];
    var toNode = nodeByIdAll[conn.to];
    if (!fromNode || !toNode) continue;
    // Tool wires keep the agent's bottom "tools" anchor → tool top, dropping
    // straight down to the centered tool row. Clear bends, don't re-aim.
    if (isToolConnection(conn, fromNode)) {
      conn.bendPoints = [];
      conn.fromAnchor = 'bottom';
      conn.toAnchor = 'top';
      continue;
    }
    if (!nodeById[conn.from] || !nodeById[conn.to]) continue;
    conn.bendPoints = [];
    var isClassifierOut = typeof conn.fromAnchor === 'string' && conn.fromAnchor.indexOf('classifier') === 0;
    var isConditionOut = fromNode.type === 'condition' || fromNode.icon === 'condition';
    // Classifier/condition outputs keep their semantic source anchor (YES/NO,
    // label) — geometry must not re-aim where the line leaves from. But a
    // back-edge OUT of one (e.g. "More PDFs?" YES looping up) still draws as a
    // full-height vertical. We may add bend points to route it cleanly below the
    // rows as long as we leave `fromAnchor` untouched; toAnchor is free.
    var semanticOut = isClassifierOut || isConditionOut;
    var fl = (fromNode.position && fromNode.position.left) || 0;
    var ft = (fromNode.position && fromNode.position.top) || 0;
    var tl = (toNode.position && toNode.position.left) || 0;
    var tt = (toNode.position && toNode.position.top) || 0;
    var dx = tl - fl;
    var dy = tt - ft;
    // Back-edge (loop): the target sits in an EARLIER layer (clearly to the
    // left), so this is a feedback wire — e.g. "More PDFs?" looping back up to
    // "Pick Current PDF". Drawn with plain anchors it becomes one line spanning
    // the whole height of the graph. Route it the same way as a long forward
    // hop: drop out the bottom, run horizontally just below the rows it spans,
    // then rise back up into the target's bottom — a tidy under-the-flow return
    // path instead of a canvas-crossing vertical.
    var backEdge = dx < -(nodeWidth(fromNode) + COLUMN_GAP_X * 0.5);
    if (backEdge) {
      var bFromCx = Math.round(fl + nodeWidth(fromNode) / 2);
      var bToCx = Math.round(tl + nodeWidth(toNode) / 2);
      var bDipY = lowestBottomBetween(bToCx, bFromCx) + ROW_GAP_Y;
      // Keep a semantic output's source anchor (YES/NO, label); only re-aim it
      // for plain nodes. The dip + target-bottom anchor make the return path
      // read as an under-the-flow loop either way.
      if (!semanticOut) conn.fromAnchor = 'bottom';
      conn.toAnchor = 'bottom';
      conn.bendPoints = [
        { x: bFromCx, y: Math.round(bDipY) },
        { x: bToCx, y: Math.round(bDipY) }
      ];
      continue;
    }
    if (semanticOut) continue;
    if (Math.abs(dx) >= Math.abs(dy)) {
      // Long horizontal hop between two nodes on (roughly) the same row would
      // draw one straight diagonal that slices through every node sitting
      // between them. Detect that case — the span clears more than a single
      // column gap while the rows stay close — and route it BELOW the row
      // instead: leave from the bottom, dip under both nodes, and come back up
      // into the target's bottom. A single bend point at the dip depth gives
      // the clean down-and-across shape (no line cutting through the row).
      var fromH = nodeHeight(fromNode);
      var toH = nodeHeight(toNode);
      var sameRow = Math.abs(dy) <= Math.max(fromH, toH);
      var longHop = Math.abs(dx) > (nodeWidth(fromNode) + COLUMN_GAP_X * 1.5);
      if (sameRow && longHop) {
        // Drop straight down from the source, run horizontally just BELOW the
        // rows it spans, then rise straight back up into the target — so the
        // line never cuts through any node between the two endpoints, while the
        // dip stays only as deep as it needs to.
        var fromCx = Math.round(fl + nodeWidth(fromNode) / 2);
        var toCx = Math.round(tl + nodeWidth(toNode) / 2);
        var dipY = lowestBottomBetween(fromCx, toCx) + ROW_GAP_Y;
        conn.fromAnchor = 'bottom';
        conn.toAnchor = 'bottom';
        conn.bendPoints = [
          { x: fromCx, y: Math.round(dipY) },
          { x: toCx, y: Math.round(dipY) }
        ];
      } else {
        conn.fromAnchor = dx >= 0 ? 'right' : 'left';
        conn.toAnchor = dx >= 0 ? 'left' : 'right';
      }
    } else {
      conn.fromAnchor = dy >= 0 ? 'bottom' : 'top';
      conn.toAnchor = dy >= 0 ? 'top' : 'bottom';
    }
  }

  // Two-way pairs: when the same neighbors are wired both directions (A->B AND
  // B->A) the geometric pass above aims both edges at the identical two dots, so
  // the lines stack into one. Route the return edge of each pair onto a
  // perpendicular arc (both endpoints on the opposite axis) so each direction
  // stays its own visible line. Prefer moving the non-semantic edge so a
  // condition's YES/NO or a classifier label keeps its meaningful anchor.
  var edgeByKey = {};
  for (var ei = 0; ei < connections.length; ei++) {
    if (connections[ei]) edgeByKey[connections[ei].from + '>' + connections[ei].to] = connections[ei];
  }
  var isSemanticEdge = function(edge) {
    var fn = edge && nodeById[edge.from];
    return !fn
      || fn.type === 'condition' || fn.icon === 'condition'
      || (typeof edge.fromAnchor === 'string' && edge.fromAnchor.indexOf('classifier') === 0);
  };
  var pairDone = {};
  for (var pi = 0; pi < connections.length; pi++) {
    var pc = connections[pi];
    if (!pc) continue;
    var reverse = edgeByKey[pc.to + '>' + pc.from];
    if (!reverse) continue;                          // no opposite edge → not a pair
    var pairKey = (pc.from < pc.to ? pc.from + '>' + pc.to : pc.to + '>' + pc.from);
    if (pairDone[pairKey]) continue;                 // pair already split
    pairDone[pairKey] = true;
    // Move whichever edge isn't carrying semantic anchors; bail if both are.
    var moveEdge = isSemanticEdge(pc) ? reverse : pc;
    if (isSemanticEdge(moveEdge)) continue;
    var horizontal = moveEdge.fromAnchor === 'left' || moveEdge.fromAnchor === 'right';
    if (horizontal) {
      moveEdge.fromAnchor = 'bottom';
      moveEdge.toAnchor = 'bottom';
    } else {
      moveEdge.fromAnchor = 'right';
      moveEdge.toAnchor = 'right';
    }
    moveEdge.bendPoints = [];
  }

  return { nodes: nodes, connections: connections };
}

// Presets the Format button steps through on each click, in order:
//   1. default     — the original left-to-right layered layout
//   2. tight       — same shape, smaller gaps (packs a wide flow closer)
//   3. serpentine  — folds long chains into rows so they stop sprawling right
// Each entry's `options` is merged over the live `{ sizes }` and handed to
// autoLayoutWorkflow, so adding a mode here is the only change needed.
var LAYOUT_PRESETS = [
  { id: 'default', label: 'Default', options: {} },
  { id: 'tight', label: 'Tighter spacing', options: { columnGapX: 44, rowGapY: 34 } },
  { id: 'serpentine', label: 'Serpentine (looping)', options: { wrapColumns: 5 } }
];

// Map nodeId → { predecessorId: true } for every transitive predecessor
// reachable by walking connections backwards. Used to scope @variable
// suggestions to nodes that actually flow into the editing node.
function computePredecessorsByNodeId(nodes, connections) {
  var reverseAdj = {};
  (connections || []).forEach(function(conn) {
    if (!conn || !conn.from || !conn.to) return;
    if (!reverseAdj[conn.to]) reverseAdj[conn.to] = [];
    reverseAdj[conn.to].push(conn.from);
  });
  var result = {};
  (nodes || []).forEach(function(node) {
    var visited = {};
    var queue = [node.id];
    while (queue.length) {
      var cur = queue.shift();
      var preds = reverseAdj[cur] || [];
      preds.forEach(function(predId) {
        if (!visited[predId]) {
          visited[predId] = true;
          queue.push(predId);
        }
      });
    }
    result[node.id] = visited;
  });
  return result;
}

// Edges that jump BACK to an earlier node (loop-backs, e.g. "More pages?"
// → restart). Identified by DFS from the graph's zero-incoming roots: an
// edge into a node still on the active DFS path closes a cycle. Keyed
// "from>to" in the returned map.
function computeBackEdgeKeys(connections) {
  var adj = {};
  var inDegree = {};
  var ids = [];
  var known = {};
  (connections || []).forEach(function(conn) {
    if (!conn || !conn.from || !conn.to) return;
    if (!adj[conn.from]) adj[conn.from] = [];
    adj[conn.from].push(conn.to);
    inDegree[conn.to] = (inDegree[conn.to] || 0) + 1;
    [conn.from, conn.to].forEach(function(id) {
      if (!known[id]) { known[id] = true; ids.push(id); }
    });
  });
  var color = {}; // undefined = unvisited, 1 = on current DFS path, 2 = done
  var backEdges = {};
  function dfs(rootId) {
    var stack = [{ id: rootId, next: 0 }];
    color[rootId] = 1;
    while (stack.length) {
      var frame = stack[stack.length - 1];
      var children = adj[frame.id] || [];
      if (frame.next >= children.length) {
        color[frame.id] = 2;
        stack.pop();
        continue;
      }
      var child = children[frame.next];
      frame.next += 1;
      if (color[child] === 1) backEdges[frame.id + '>' + child] = true;
      else if (!color[child]) {
        color[child] = 1;
        stack.push({ id: child, next: 0 });
      }
    }
  }
  ids.forEach(function(id) { if (!inDegree[id] && !color[id]) dfs(id); });
  ids.forEach(function(id) { if (!color[id]) dfs(id); }); // fully-cyclic leftovers
  return backEdges;
}

// Ids of every node that flows into `nodeId`, nearest hop first (BFS over
// connections walked backwards). Loop-back edges are excluded: a node
// reachable only by going AROUND a loop runs in a later iteration, not
// before the target. Used to scope the browser editors' "Continue from"
// dropdown to tabs that exist before the node runs — the first browser
// node in this order is the Auto ("last used tab") target.
function computeUpstreamIdsNearestFirst(nodeId, connections) {
  var backEdges = computeBackEdgeKeys(connections);
  var reverseAdj = {};
  (connections || []).forEach(function(conn) {
    if (!conn || !conn.from || !conn.to) return;
    if (backEdges[conn.from + '>' + conn.to]) return;
    if (!reverseAdj[conn.to]) reverseAdj[conn.to] = [];
    reverseAdj[conn.to].push(conn.from);
  });
  var visited = {};
  visited[nodeId] = true;
  var order = [];
  var queue = [nodeId];
  while (queue.length) {
    var cur = queue.shift();
    (reverseAdj[cur] || []).forEach(function(predId) {
      if (visited[predId]) return;
      visited[predId] = true;
      order.push(predId);
      queue.push(predId);
    });
  }
  return order;
}

// Build the @variable suggestion list for the editor of `nodeId`.
// Returns variable nodes (always all of them) + previous-step output nodes
// (only those that transitively connect into `nodeId`). Each entry is
// optionally enriched with `_runtimeOutput` so the field picker can drill
// into structured values, and previous-step entries are tagged with
// `_isNodeOutput: true` so the dropdown can render them in a separate
// "From previous steps" section.
function buildEditorContextNodes(nodeId, nodes, connections, nodeOutputById, variableValuesByName) {
  var safeNodes = Array.isArray(nodes) ? nodes : [];
  var outputs = nodeOutputById || {};
  var varValues = variableValuesByName || {};
  var safeConns = Array.isArray(connections) ? connections : [];
  var predecessorIds = nodeId
    ? (computePredecessorsByNodeId(safeNodes, connections)[nodeId] || {})
    : {};

  // If this node is a TOOL of a Tool Calling agent (wired from the agent's bottom
  // anchor), its real runtime input is the value the AGENT passes it — NOT the
  // agent's own output ({answer, toolCalls}). Treating the agent's output as the
  // tool's input misleads AskCode into reasoning about the wrong shape. So drop
  // the owning agent(s) from the predecessor set here; the tool reads inputs[0]
  // as the agent's argument (the node's code/contract already describes that).
  if (nodeId) {
    var nodeById = {};
    safeNodes.forEach(function(n) { if (n && n.id != null) nodeById[String(n.id)] = n; });
    safeConns.forEach(function(c) {
      if (!c || String(c.to) !== String(nodeId) || String(c.fromAnchor || '') !== 'bottom') return;
      var fromNode = nodeById[String(c.from)];
      if (fromNode && fromNode.icon === 'tool-agent') {
        delete predecessorIds[String(c.from)];
      }
    });
  }

  var variableNodes = safeNodes.filter(function(n) {
    return n && n.kind === 'workflow-node' && n.icon === 'variables';
  });
  var enrichedVarNodes = variableNodes.map(function(vn) {
    var varName = String(vn.title || '').trim();
    var hasRuntime = varName && Object.prototype.hasOwnProperty.call(varValues, varName);
    var runtimeVal = hasRuntime ? varValues[varName] : undefined;
    if (runtimeVal !== undefined && runtimeVal !== null && typeof runtimeVal === 'object') {
      return Object.assign({}, vn, { _runtimeOutput: runtimeVal });
    }
    return vn;
  });

  var outputNodes = safeNodes.filter(function(n) {
    return predecessorIds[n.id] &&
      n.kind === 'workflow-node' &&
      n.icon !== 'variables' &&
      n.icon !== 'start' &&
      n.icon !== 'condition';
  }).map(function(n) {
    return Object.assign({}, n, {
      _isNodeOutput: true,
      _runtimeOutput: outputs[n.id]
    });
  });

  return enrichedVarNodes.concat(outputNodes);
}

// When a new connection lands on a Set Variable node, flip its Source to
// "From input" (source="true") so the incoming data is actually captured.
// Without this the node defaults to Manual mode and silently ignores the
// upstream value — see useConnectionManager.toggleConnectionNoData for the
// matching behavior on the noData toggle.
//
// Skipped when the new connection is noData=true (trigger-only edge — Manual
// mode is the right default there).
function autoSetVariableSourceForTarget(nodes, targetId, isNoData) {
  if (isNoData || !Array.isArray(nodes) || !targetId) return nodes;
  return nodes.map(function(n) {
    if (!n || n.id !== targetId) return n;
    if (n.templateId !== 'sys-set-variable') return n;
    var pv = n.paramValues || {};
    if (String(pv.source || 'false') === 'true') return n;
    return Object.assign({}, n, {
      paramValues: Object.assign({}, pv, { source: 'true' })
    });
  });
}

// Resolve a dotted path ("a.b.c") inside a value. Returns undefined when any
// segment is missing. Matches the api's extractPropPath so a connection's
// propPath resolves to the same value the runtime would write.
function getValueAtDotPath(value, path) {
  if (value === null || value === undefined || !path) return value;
  var parts = String(path).split('.').filter(Boolean);
  var cur = value;
  for (var i = 0; i < parts.length; i++) {
    if (cur === null || cur === undefined) return undefined;
    cur = cur[parts[i]];
  }
  return cur;
}

// Resolve what an incoming connection feeds into a Set Variable node: the
// source node's output, narrowed by the connection's propPath, plus the node's
// Mode. Returns { varName, input, mode } or null. Acts only in "From input"
// mode with an upstream node that has output.
function resolveSetVariableInput(setVar, connections, nodeOutputById) {
  if (!setVar) return null;
  if (setVar.templateId !== 'sys-set-variable' && setVar.icon !== 'set-variable') return null;
  var pv = setVar.paramValues || {};
  if (String(pv.source || 'false') !== 'true') return null; // only "From input"
  var varName = String(pv.variable || '').trim();
  if (!varName) return null;

  var outputs = nodeOutputById || {};
  var conns = Array.isArray(connections) ? connections : [];
  var dataConn = null;
  for (var i = 0; i < conns.length; i++) {
    var c = conns[i];
    if (!c || c.to !== setVar.id || c.noData) continue;
    var out = outputs[c.from];
    if (out !== null && out !== undefined) { dataConn = c; break; }
  }
  if (!dataConn) return null;

  var resolved = dataConn.propPath
    ? getValueAtDotPath(outputs[dataConn.from], dataConn.propPath)
    : outputs[dataConn.from];
  if (resolved === undefined) return null;
  return { varName: varName, input: resolved, mode: String(pv.mode || 'replace') };
}

// The value a target Variable becomes once a Set Variable applies — computed
// EXACTLY like the runtime (apply Mode against the variable's current value,
// then coerce to the variable's declared type). The variable's type is never
// changed, matching runtime. Returns the typed value, or undefined.
function resolveSetVariableTargetValue(resolved, targetVar) {
  if (!resolved || !targetVar) return undefined;
  var npv = targetVar.paramValues || {};
  var dataType = String(npv.dataType || 'string');
  var currentValue = coerceValueForVariableType(dataType, npv.value);
  return computeSetVariableResult(currentValue, resolved.input, resolved.mode, dataType);
}

// Resolve what a Set Variable should do to its target variable, computed like
// the runtime (Mode + type coercion). This is EVENT-DRIVEN only — call it when
// the user connects a wire to the Set Variable or picks the target from the
// dropdown. Returns:
//   { nextNodes, tempPreview }
// - PERSISTENT target → the value is baked into the variable node's value
//   (persisted, like applyStaticVariableValue); nextNodes carries that edit.
// - TEMPORARY target → the value must NOT be saved, so nextNodes only clears any
//   stale saved value and tempPreview = { varName, value } is returned for the
//   caller to push into the ephemeral live map (variableValuesByName).
// nextNodes is the original reference when no node edit is needed.
function computeSetVariableAutofill(nodes, connections, setVarNodeId, nodeOutputById) {
  var result = { nextNodes: nodes, tempPreview: null };
  if (!Array.isArray(nodes) || !setVarNodeId) return result;
  var setVar = null;
  for (var s = 0; s < nodes.length; s++) {
    if (nodes[s] && nodes[s].id === setVarNodeId) { setVar = nodes[s]; break; }
  }
  var resolved = resolveSetVariableInput(setVar, connections, nodeOutputById);
  if (!resolved) return result;

  var targetVar = null;
  for (var v = 0; v < nodes.length; v++) {
    var vn = nodes[v];
    if (vn && vn.icon === 'variables' && String(vn.title || '').trim() === resolved.varName) { targetVar = vn; break; }
  }
  if (!targetVar) return result;

  var npv = targetVar.paramValues || {};
  var isPersistent = String(npv.retain || 'false') === 'true';
  var value = resolveSetVariableTargetValue(resolved, targetVar);

  // The source output may be the truncated structure-preview cache
  // (node.lastOutput.shape, clipped after a reload) rather than the real
  // runtime value. Baking that over the variable would silently corrupt it
  // with a cut-off value, so leave the variable untouched until a real run
  // supplies the full value.
  if (isTruncatedShapeValue(value)) return result;

  if (isPersistent) {
    var dataType = String(npv.dataType || 'string');
    var nextValue = formatValueForStorage(dataType, value);
    if (String(npv.value) !== nextValue) {
      result.nextNodes = nodes.map(function(n) {
        return n === targetVar
          ? Object.assign({}, n, { paramValues: Object.assign({}, npv, { value: nextValue }) })
          : n;
      });
    }
    return result;
  }

  // Temporary: live preview only (never saved). Hand the value back for the
  // ephemeral map and make sure no stale saved value lingers on the node.
  result.tempPreview = { varName: resolved.varName, value: value };
  if (npv.value !== undefined && npv.value !== null && npv.value !== '') {
    result.nextNodes = nodes.map(function(n) {
      return n === targetVar
        ? Object.assign({}, n, { paramValues: Object.assign({}, npv, { value: '' }) })
        : n;
    });
  }
  return result;
}

// Clear the saved value of any TEMPORARY variable fed by a "From input" Set
// Variable that has an incoming data connection: its value is produced at
// runtime by that node, so a saved seed is meaningless and would make a
// Temporary variable wrongly persist data. The live value still shows via
// computeSetVariablePreviews. Returns a new nodes array, or the original ref.
// Used by normalizeWorkflowState so stale values are scrubbed on load/save.
function clearFedTemporaryVariableValues(nodes, connections) {
  if (!Array.isArray(nodes)) return nodes;
  var conns = Array.isArray(connections) ? connections : [];
  var fed = {};
  for (var i = 0; i < nodes.length; i++) {
    var sv = nodes[i];
    if (!sv || (sv.templateId !== 'sys-set-variable' && sv.icon !== 'set-variable')) continue;
    var pv = sv.paramValues || {};
    if (String(pv.source || 'false') !== 'true') continue;
    var name = String(pv.variable || '').trim();
    if (!name) continue;
    for (var c = 0; c < conns.length; c++) {
      var conn = conns[c];
      if (conn && conn.to === sv.id && !conn.noData) { fed[name] = true; break; }
    }
  }
  if (Object.keys(fed).length === 0) return nodes;
  var changed = false;
  var next = nodes.map(function(n) {
    if (!n || n.icon !== 'variables') return n;
    var npv = n.paramValues || {};
    if (String(npv.retain || 'false') === 'true') return n; // persistent keeps its value
    if (!fed[String(n.title || '').trim()]) return n;
    if (npv.value === undefined || npv.value === null || npv.value === '') return n;
    changed = true;
    return Object.assign({}, n, { paramValues: Object.assign({}, npv, { value: '' }) });
  });
  return changed ? next : nodes;
}

// Pick the Variable node dataType that best fits a sample value, using the same
// canonical type names the Variables panel offers (string/int/bool/json/array).
// Only used to initialise a NEWLY created variable sensibly — existing variables
// keep their declared type (runtime coerces the value into it).
function dataTypeFromValue(value) {
  if (Array.isArray(value)) return 'array';
  if (value !== null && typeof value === 'object') return 'json';
  if (typeof value === 'number') return 'int';
  if (typeof value === 'boolean') return 'bool';
  return 'string';
}

// Build the nodes + connection that capture a source node's output (optionally
// narrowed to a dot-path) into a workflow variable via a Set Variable node.
// Mirrors how the planner/connect flow wires Set Variable: the node references
// its target variable BY NAME (paramValues.variable) and pulls its data from an
// incoming connection (source:'true' + conn.propPath). The caller then runs
// computeSetVariableAutofill on the returned setVarId to fill the live value.
// opts: { sourceNodeId, propPath, targetName, createVariable, sampleValue,
//         variableTemplate, setVariableTemplate }
// Returns { nodes, connections, setVarId } (new arrays) or null when invalid.
function buildSetVariableForOutput(nodes, connections, opts) {
  if (!Array.isArray(nodes) || !opts || !opts.sourceNodeId || !opts.setVariableTemplate) return null;
  var targetName = String(opts.targetName || '').trim();
  if (!targetName) return null;

  var source = null;
  for (var i = 0; i < nodes.length; i++) {
    if (nodes[i] && nodes[i].id === opts.sourceNodeId) { source = nodes[i]; break; }
  }
  if (!source) return null;

  var seed = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
  var nextNodes = nodes.slice();

  // 1) Ensure the target variable node exists (create only when asked and a
  //    variable with that name isn't already present — typing an existing name
  //    safely reuses it instead of making a duplicate).
  var hasVar = nextNodes.some(function(n) {
    return n && n.icon === 'variables' && String(n.title || '').trim() === targetName;
  });
  if (opts.createVariable && !hasVar && opts.variableTemplate) {
    var varNode = cloneTemplateNode(opts.variableTemplate, 'sys-variables-' + seed, { left: 0, top: 0 });
    varNode.title = targetName;
    varNode.paramValues = Object.assign({}, varNode.paramValues || {}, {
      dataType: dataTypeFromValue(opts.sampleValue),
      retain: 'false',
      value: ''
    });
    nextNodes = nextNodes.concat(varNode);
  }

  // 2) Place the Set Variable node below the source, fanning siblings sideways
  //    so repeated captures from the same node don't stack on top of each other.
  var size = source.size && Number.isFinite(source.size.height)
    ? source.size : { width: 132, height: 132 };
  var srcPos = source.position || { left: 0, top: 0 };
  var setVarLookup = {};
  nextNodes.forEach(function(n) {
    if (n && (n.templateId === 'sys-set-variable' || n.icon === 'set-variable')) setVarLookup[n.id] = true;
  });
  var conns = Array.isArray(connections) ? connections : [];
  var siblingCount = conns.filter(function(c) {
    return c && c.from === source.id && setVarLookup[c.to];
  }).length;
  var pos = {
    left: Math.round(srcPos.left + siblingCount * (size.width + 40)),
    top: Math.round(srcPos.top + size.height + 90)
  };

  var setVarId = 'sys-set-variable-' + seed;
  var setVarNode = cloneTemplateNode(opts.setVariableTemplate, setVarId, pos);
  setVarNode.paramValues = Object.assign({}, setVarNode.paramValues || {}, {
    variable: targetName,
    source: 'true',
    value: ''
  });
  // Bake the title so the node reads "Set <var>" right away (mirrors how a
  // condition node titles itself when its variable is picked) — otherwise it
  // keeps the template's generic "Set Variable" until something re-derives it.
  setVarNode.title = compactSetVariableTitle(setVarNode);
  nextNodes = nextNodes.concat(setVarNode);

  // 3) Wire source -> Set Variable, narrowed to the picked field's path.
  var conn = { id: 'setvar-conn-' + seed, from: source.id, to: setVarId, fromAnchor: 'bottom', toAnchor: 'top' };
  var propPath = String(opts.propPath || '').trim();
  if (propPath) conn.propPath = propPath;

  return { nodes: nextNodes, connections: conns.concat(conn), setVarId: setVarId };
}

// When a node is deleted, bridge each (predecessor -> deletedNode -> successor)
// pair so the workflow stays connected. Preserves the predecessor's outgoing
// anchor/branch and the successor's incoming anchor. Skips bridges that would
// duplicate an existing connection.
function bridgeConnectionsAroundNodes(connections, deletedIdSet) {
  var list = Array.isArray(connections) ? connections : [];
  var kept = [];
  var incomingByDeleted = {};
  var outgoingByDeleted = {};
  for (var i = 0; i < list.length; i++) {
    var c = list[i];
    if (!c) continue;
    var fromDeleted = !!deletedIdSet[c.from];
    var toDeleted = !!deletedIdSet[c.to];
    if (!fromDeleted && !toDeleted) { kept.push(c); continue; }
    if (toDeleted && !fromDeleted) {
      (incomingByDeleted[c.to] = incomingByDeleted[c.to] || []).push(c);
    }
    if (fromDeleted && !toDeleted) {
      (outgoingByDeleted[c.from] = outgoingByDeleted[c.from] || []).push(c);
    }
  }
  var existingKey = {};
  for (var k = 0; k < kept.length; k++) {
    var ek = kept[k];
    existingKey[ek.from + '>' + ek.to + '>' + (ek.fromAnchor || '') + '>' + (ek.toAnchor || '')] = true;
  }
  var bridgeCounter = 0;
  Object.keys(incomingByDeleted).forEach(function(deletedId) {
    var ins = incomingByDeleted[deletedId] || [];
    var outs = outgoingByDeleted[deletedId] || [];
    for (var a = 0; a < ins.length; a++) {
      for (var b = 0; b < outs.length; b++) {
        var inC = ins[a];
        var outC = outs[b];
        // Don't chain through another deleted node — skip if the successor is also deleted.
        if (deletedIdSet[outC.to]) continue;
        // Don't bridge to self.
        if (inC.from === outC.to) continue;
        var bridged = {
          id: 'bridge-conn-' + Date.now().toString(36) + '-' + (bridgeCounter++),
          from: inC.from,
          to: outC.to,
          fromAnchor: inC.fromAnchor,
          toAnchor: outC.toAnchor
        };
        if (inC.branch != null) bridged.branch = inC.branch;
        var key = bridged.from + '>' + bridged.to + '>' + (bridged.fromAnchor || '') + '>' + (bridged.toAnchor || '');
        if (existingKey[key]) continue;
        existingKey[key] = true;
        kept.push(bridged);
      }
    }
  });
  return kept;
}

// Lockstep helper: mirror a per-instance param value into the matching
// hyperparameter's `default` so the "Edit this step" modal (which edits
// `default`) and the sidebar/canvas (which edit `paramValues`) never disagree.
// Returns a NEW hyperparameters array when a matching key changed, otherwise
// the original reference (so callers can skip a needless re-render). A no-op
// for nodes whose key isn't a declared hyperparameter (e.g. set-variable /
// condition internal keys).
function syncHyperparamDefault(hyperparameters, key, value) {
  if (!Array.isArray(hyperparameters)) return hyperparameters;
  var changed = false;
  var next = hyperparameters.map(function(p) {
    if (!p || p.key !== key || p.default === value) return p;
    changed = true;
    return Object.assign({}, p, { default: value });
  });
  return changed ? next : hyperparameters;
}

module.exports = {
  fallbackSizes: fallbackSizes,
  FUTURE_CONNECTOR_BUTTON_WIDTH: FUTURE_CONNECTOR_BUTTON_WIDTH,
  FUTURE_CONNECTOR_BUTTON_HEIGHT: FUTURE_CONNECTOR_BUTTON_HEIGHT,
  cloneTemplateNode: cloneTemplateNode,
  getFutureNodeSize: getFutureNodeSize,
  getFutureNodePosition: getFutureNodePosition,
  compactSetVariableTitle: compactSetVariableTitle,
  compactConditionTitle: compactConditionTitle,
  autoFieldName: autoFieldName,
  buildGeneratedAgentTemplate: buildGeneratedAgentTemplate,
  buildGeneratedCustomTemplate: buildGeneratedCustomTemplate,
  remapClassifierConnections: remapClassifierConnections,
  bridgeConnectionsAroundNodes: bridgeConnectionsAroundNodes,
  computeDownstreamFlow: computeDownstreamFlow,
  autoLayoutWorkflow: autoLayoutWorkflow,
  LAYOUT_PRESETS: LAYOUT_PRESETS,
  computePredecessorsByNodeId: computePredecessorsByNodeId,
  computeUpstreamIdsNearestFirst: computeUpstreamIdsNearestFirst,
  buildEditorContextNodes: buildEditorContextNodes,
  autoSetVariableSourceForTarget: autoSetVariableSourceForTarget,
  computeSetVariableAutofill: computeSetVariableAutofill,
  clearFedTemporaryVariableValues: clearFedTemporaryVariableValues,
  dataTypeFromValue: dataTypeFromValue,
  buildSetVariableForOutput: buildSetVariableForOutput,
  syncHyperparamDefault: syncHyperparamDefault
};
